/** The languages the backoffice is translated into. The API accepts the same ones for a user's language */
export const LANGUAGES = ['fr', 'en'] as const;
export type Language = typeof LANGUAGES[number];

export const DEFAULT_LANGUAGE: Language = 'fr';

/** Each language written in itself, for the language pickers */
export const LANGUAGE_NAMES: Record<Language, string> = { fr: 'Français', en: 'English' };

export const isLanguage = (value: unknown): value is Language => LANGUAGES.includes(value as Language);

/**
 * The language to show: the one chosen on the user's account, else the last one chosen in this browser, else French.
 * Users stored before languages existed, and sessions saved before, have no account language.
 */
export const resolveLanguage = (accountLanguage: unknown, browserLanguage: unknown): Language => {
  if (isLanguage(accountLanguage)) return accountLanguage;
  if (isLanguage(browserLanguage)) return browserLanguage;
  return DEFAULT_LANGUAGE;
};

/** The locale dates are written in: the browser's own variant of the language (fr-CA, en-GB…) when it has one */
export const localeOf = (language: Language, browserLocales: readonly string[]): string =>
  browserLocales.find(locale => locale.split('-')[0].toLowerCase() === language) ?? (language === 'fr' ? 'fr-FR' : 'en-US');

const STORAGE_KEY = 'backoffice.language';

/** The language last chosen in this browser, which the sign-in screen starts in */
export const readBrowserLanguage = (): string | null => {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
};

export const writeBrowserLanguage = (language: Language) => {
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // Storage blocked: the language still applies until the page is reloaded
  }
};
